import { useEffect, useState } from "react";
import { fetchDcIn, createDcIn } from "../../services/api/boq/dcInApi";
import { getVendors } from "../../services/api/boq/VendorService";
import {
  getPurchaseOrderById,
  getPurchaseOrders,
} from "../../services/api/boq/purchaseService";
import { fetchBoqByProject } from "../../services/api/boq/boqService";
import { fetchProjectsApi } from "../../services/api/boq/projectService";
import toast from "react-hot-toast";
import { useSearchParams } from "react-router-dom";

const DcInPage = () => {
  const [dcList, setDcList] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [pos, setPos] = useState([]);
  const [projects, setProjects] = useState([]);
  const [boqs, setBoqs] = useState([]);

  const [showDrawer, setShowDrawer] = useState(false);
  const [viewDc, setViewDc] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const perPage = 10;
  const [pagination, setPagination] = useState({});
  const [loadingList, setLoadingList] = useState(false);
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [searchParams] = useSearchParams();
  const poIdFromUrl = searchParams.get("po_id");

  const [formData, setFormData] = useState({
    project_id: "",
    vendor_id: "",
    po_id: "",
    delivery_channel: "vendor",
    items: [],
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);

    return () => clearTimeout(timer);
  }, [search]);

  // ========== LOAD DATA ==========
  useEffect(() => {
    loadData(page);
  }, [page, debouncedSearch]);

  useEffect(() => {
    if (poIdFromUrl) {
      setShowDrawer(true); // Open the creation drawer
      handleAutoPopulate(poIdFromUrl);
    }
  }, [poIdFromUrl]);

  const handleAutoPopulate = async (id) => {
    try {
      const response = await getPurchaseOrderById(id);
      const po = response.data;

      // Populate items that have pending quantity
      const formattedItems = po.items
        .filter((item) => Number(item.pending_qty) > 0)
        .map((item) => ({
          boq_id: item.boq_item_id,
          item_name: item.item_name,
          ordered_qty: item.ordered_qty,
          received_qty: item.received_qty || 0,
          pending_qty: item.pending_qty,
          qty: "",
        }));

      // Update Form Data with PO details
      setFormData({
        project_id: po.project_id,
        vendor_id: po.vendor_id,
        po_id: id,
        delivery_channel: "vendor",
        items: formattedItems,
      });

      // Also trigger BOQ fetch for that project if needed by your UI
      const boqData = await fetchBoqByProject(po.project_id);
      setBoqs(boqData);
    } catch (err) {
      console.error("Error auto-populating DC-IN:", err);
      toast.error("Failed to load Purchase Order details");
    }
  };
  const fetchVendors = async () => {
    let page = 1;
    let allVendors = [];
    let lastPage = 1;

    try {
      do {
        const res = await getVendors(page);

        const apiData = res?.data?.data;
        const vendors = apiData?.data ?? [];

        allVendors = [...allVendors, ...vendors];

        lastPage = apiData?.last_page;
        page++;
      } while (page <= lastPage);

      return allVendors; // ✅ IMPORTANT
    } catch (err) {
      console.error("Error fetching vendors:", err);
      return [];
    }
  };

  const fetchAllDc = async () => {
    let page = 1;
    let allData = [];
    let lastPage = 1;

    do {
      const res = await fetchDcIn(page);

      const apiData = res?.data;
      const data = apiData?.data || [];

      allData = [...allData, ...data];

      lastPage = res?.data?.last_page || 1;

      page++;
    } while (page <= lastPage);

    return allData;
  };

  const loadData = async (pageNo = 1) => {
    try {
      setLoadingList(true);

      let dcArray = [];
      let paginationData = {};

      if (debouncedSearch) {
        // 🔍 SEARCH MODE → ALL DATA
        dcArray = await fetchAllDc();

        paginationData = {
          current_page: 1,
          last_page: 1,
          total: dcArray.length,
        };
      } else {
        // 📄 NORMAL MODE → BACKEND PAGINATION
        const dcRes = await fetchDcIn(pageNo);

        const apiData = dcRes?.data;

        dcArray = apiData?.data || [];
        paginationData = apiData || {};
      }

      const [vendorsList, posRes, pr] = await Promise.all([
        fetchVendors(),
        getPurchaseOrders(),
        fetchProjectsApi(),
      ]);

      const posList = posRes?.data?.data || [];
      const projectsList = pr?.data?.data?.data || [];

      const vendorsMap = vendorsList.reduce((acc, v) => {
        acc[v.id] = v.name;
        return acc;
      }, {});

      const posMap = posList.reduce((acc, po) => {
        acc[po.id] = po.po_number;
        return acc;
      }, {});

      let formattedDc = dcArray.map((dc) => ({
        ...dc,
        vendor_name: vendorsMap[dc.vendor_id] || "",
        po_number: posMap[dc.purchase_order_id] || "",
      }));

      // 🔍 FRONTEND SEARCH FILTER
      if (debouncedSearch) {
        const term = debouncedSearch.toLowerCase();

        formattedDc = formattedDc.filter(
          (dc) =>
            dc.vendor_name?.toLowerCase().includes(term) ||
            dc.po_number?.toLowerCase().includes(term) ||
            dc.delivery_channel?.toLowerCase().includes(term) ||
            dc.dc_number?.toLowerCase().includes(term),
        );
      }

      setDcList(formattedDc);
      setPagination(paginationData);
      setVendors(vendorsList);
      setPos(posList);
      setProjects(projectsList);
    } catch (err) {
      console.error("Error loading data:", err);
    } finally {
      setLoadingList(false);
    }
  };

  // ========== FORM HANDLING ==========
  const handleChange = async (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({ ...prev, [name]: value }));

    if (name === "project_id") {
      const boqData = await fetchBoqByProject(value);
      setBoqs(boqData);
    }

    if (name === "po_id") {
      const response = await getPurchaseOrderById(value);
      const po = response.data;
      // const formattedItems = po.items.map((items) => ({
      //   boq_id: items.boq_item_id,
      //   item_name: items.item_name,
      //   ordered_qty: items.ordered_qty,
      //   received_qty: items.received_qty || 0,
      //   qty: "",
      // }));
      const formattedItems = po.items
        .filter((item) => Number(item.pending_qty) > 0)
        .map((item) => ({
          boq_id: item.boq_item_id,
          item_name: item.item_name,
          ordered_qty: item.ordered_qty,
          received_qty: item.received_qty || 0,
          pending_qty: item.pending_qty,
          qty: "", // This is the 'Receive Now' input value
        }));
      setFormData((prev) => ({
        ...prev,
        po_id: value,
        items: formattedItems,
      }));
    }
  };

  const handleItemChange = (index, field, value) => {
    const updated = [...formData.items];
    updated[index][field] = value;
    setFormData({ ...formData, items: updated });
  };

  // const handleSubmit = async (e) => {
  //   e.preventDefault();
  //   setLoading(true);

  //   const payload = {
  //     ...formData,
  //     project_id: String(formData.project_id), // keep consistent
  //     vendor_id: Number(formData.vendor_id),
  //     po_id: Number(formData.po_id),
  //     items: formData.items.map((item) => ({
  //       boq_id: Number(item.boq_id) || 0, // ALWAYS send
  //       item_name: item.item_name || "", // ALWAYS send
  //       qty: Number(item.qty) || 0,
  //     })),
  //   };

  //   await createDcIn(payload);
  //   loadData(page); // Refresh list after creation
  //   toast.success("✅ DC In Created Successfully");

  //   setShowDrawer(false);
  //   setFormData({
  //     project_id: "",
  //     vendor_id: "",
  //     po_id: "",
  //     delivery_channel: "vendor",
  //     items: [],
  //   });

  //   loadData(1);
  //   setLoading(false);
  // };
  const handleSubmit = async (e) => {
    e.preventDefault();

    // 1. Filter out items where 'Receive Now' (qty) is 0 or empty
    const receivedItems = formData.items
      .filter((item) => Number(item.qty) > 0)
      .map((item) => ({
        boq_id: Number(item.boq_id) || 0, // ALWAYS send
        item_name: item.item_name || "", // ALWAYS send
        qty: Number(item.qty),
      }));

    // 2. Validation: Prevent saving if no items are being received
    if (receivedItems.length === 0) {
      toast.error("Please enter a quantity for at least one item.");
      return;
    }

    setLoading(true); //

    try {
      const payload = {
        ...formData,
        project_id: String(formData.project_id), // keep consistent
        vendor_id: Number(formData.vendor_id), //
        po_id: Number(formData.po_id), //
        items: receivedItems, // Send ONLY the items with quantity > 0
      };

      await createDcIn(payload); //
      toast.success("✅ DC In Created Successfully"); //

      // Reset UI and data
      setShowDrawer(false); //
      setFormData({
        project_id: "",
        vendor_id: "",
        po_id: "",
        delivery_channel: "vendor",
        items: [],
      });

      loadData(1); // Refresh the list
    } catch (error) {
      console.error("DC In creation failed:", error);
      toast.error("Failed to create DC In entry.");
    } finally {
      setLoading(false); //
    }
  };

  return (
    <div className="p-6 bg-gray-100 min-h-screen">
      {/* HEADER */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold">DC In Management</h2>
          <p className="text-gray-500">Manage inward delivery challans</p>
        </div>
        <button
          className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded shadow"
          onClick={() => setShowDrawer(true)}
        >
          + Create DC In
        </button>
      </div>

      {/* SEARCH */}
      <div className="mb-4">
        <div className="relative">
          <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400">
            🔍
          </span>
          <input
            type="text"
            placeholder="Search by Vendor..."
            className="pl-10 pr-4 py-2 w-full rounded shadow-sm border border-gray-300 focus:ring-2 focus:ring-blue-400 focus:border-blue-400"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {/* TABLE */}
      <div className="bg-white shadow rounded overflow-x-auto">
        <table className="min-w-full table-auto divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">
                #
              </th>
              <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">
                DC Number
              </th>
              <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">
                Vendor
              </th>
              <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">
                PO
              </th>
              <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">
                Channel
              </th>
              <th className="px-4 py-2 text-right text-sm font-medium text-gray-700">
                Action
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {loadingList ? (
              <tr>
                <td colSpan={6} className="text-center py-10">
                  <div className="flex justify-center space-x-2">
                    <div className="w-3 h-3 bg-blue-500 rounded-full animate-bounce"></div>
                    <div className="w-3 h-3 bg-blue-500 rounded-full animate-bounce delay-100"></div>
                    <div className="w-3 h-3 bg-blue-500 rounded-full animate-bounce delay-200"></div>
                  </div>
                </td>
              </tr>
            ) : dcList.length === 0 ? (
              <tr>
                <td colSpan={6} className="text-center py-6 text-gray-400">
                  No Delivery Challans Found
                </td>
              </tr>
            ) : (
              dcList.map((dc, index) => (
                <tr key={dc.id} className="hover:bg-gray-50">
                  <td className="px-4 py-2">
                    {debouncedSearch
                      ? index + 1
                      : (pagination.from || 0) + index}
                  </td>
                  <td className="px-4 py-2 font-medium">{dc.dc_number}</td>
                  <td className="px-4 py-2 font-medium">{dc.vendor_name}</td>
                  <td className="px-4 py-2">{dc.po_number}</td>
                  <td className="px-4 py-2">
                    <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs">
                      {dc.delivery_channel}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-right">
                    <button
                      className="bg-gray-100 hover:bg-gray-200 px-3 py-1 rounded text-sm"
                      onClick={() => setViewDc(dc)}
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {/* Pagination */}
        {!debouncedSearch && (
          <div className="flex justify-end items-center p-4 space-x-2">
            <button
              disabled={!pagination.prev_page_url}
              onClick={() => setPage(page - 1)}
            >
              Prev
            </button>

            <span>
              Page {pagination.current_page} of {pagination.last_page}
            </span>

            <button
              disabled={!pagination.next_page_url}
              onClick={() => setPage(page + 1)}
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* DRAWER */}
      {showDrawer && (
        <>
          {/* Overlay */}
          <div
            className="fixed inset-0 bg-black bg-opacity-30 z-40"
            onClick={() => setShowDrawer(false)}
          />
          {/* Drawer Panel */}
          <div className="fixed right-0 top-0 h-full w-400 bg-white shadow-xl z-50 flex flex-col">
            <div className="flex justify-between items-center p-4 border-b">
              <h3 className="font-bold text-lg">Create DC In</h3>
              <button
                className="text-gray-500 hover:text-gray-700"
                onClick={() => setShowDrawer(false)}
              >
                ✕
              </button>
            </div>
            <form
              className="flex-1 overflow-y-auto p-4"
              onSubmit={handleSubmit}
            >
              {/* Project */}
              <div className="mb-4">
                <label className="block mb-1 font-medium">Project</label>
                <select
                  name="project_id"
                  value={formData.project_id || ""}
                  onChange={handleChange}
                  className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
                  required
                >
                  <option value="">Select Project</option>
                  {projects.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.project_name}
                    </option>
                  ))}
                </select>
              </div>
              {/* Vendor */}
              <div className="mb-4">
                <label className="block mb-1 font-medium">Vendor</label>
                <select
                  name="vendor_id"
                  value={formData.vendor_id}
                  onChange={handleChange}
                  className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
                  required
                >
                  <option value="">Select Vendor</option>
                  {vendors.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.name}
                    </option>
                  ))}
                </select>
              </div>
              {/* PO */}
              <div className="mb-4">
                <label className="block mb-1 font-medium">PO</label>
                <select
                  name="po_id"
                  value={formData.po_id}
                  onChange={handleChange}
                  className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
                  required
                >
                  <option value="">Select PO</option>
                  {pos.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.po_number}
                    </option>
                  ))}
                </select>
              </div>

              {/* Items Table */}
              <h4 className="font-semibold mb-2">Items</h4>
              <div className="overflow-x-auto mb-4">
                <table className="min-w-full table-auto border border-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left">Sl No</th>
                      <th className="px-3 py-2 border text-left text-sm">
                        Item
                      </th>
                      <th className="px-3 py-2 border text-center text-sm">
                        Ordered
                      </th>
                      <th className="px-3 py-2 border text-center text-sm">
                        Received
                      </th>
                      <th className="px-3 py-2 border text-center text-sm">
                        Receive Now
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {formData.items.map((item, index) => (
                      <tr key={index}>
                        <td className="px-3 py-2 border">{index + 1}</td>
                        <td className="px-3 py-2 border">{item.item_name}</td>
                        <td className="px-3 py-2 border text-center">
                          {item.ordered_qty}
                        </td>
                        <td className="px-3 py-2 border text-center">
                          {item.received_qty}
                        </td>
                        {/* <td className="px-3 py-2 border text-center">
                          <input
                            type="number"
                            value={item.qty}
                            max={item.ordered_qty - item.received_qty}
                            onChange={(e) =>
                              handleItemChange(index, "qty", e.target.value)
                            }
                            className="w-20 border rounded px-2 py-1 text-center focus:outline-none focus:ring-2 focus:ring-blue-400"
                            required
                          />
                        </td> */}
                        {/* <td className="px-3 py-2 border text-center">
                          <input
                            type="number"
                            value={item.qty}
                            max={item.pending_qty}
                            min="1"
                            placeholder={`Max ${item.pending_qty}`}
                            onChange={(e) => {
                              const val = Number(e.target.value);
                              if (val <= item.pending_qty && val >= 0) {
                                handleItemChange(index, "qty", e.target.value);
                              }
                            }}
                            className="w-24 border rounded px-2 py-1 text-center focus:outline-none focus:ring-2 focus:ring-blue-400"
                            required
                          />
                        </td> */}
                        <td className="px-3 py-2 border text-center">
                          <input
                            type="number"
                            value={item.qty}
                            max={item.pending_qty}
                            min="0" // Allow zero
                            placeholder={`Max ${item.pending_qty}`}
                            onChange={(e) => {
                              const val = e.target.value;
                              // Allow empty string or numbers within range
                              if (
                                val === "" ||
                                (Number(val) <= item.pending_qty &&
                                  Number(val) >= 0)
                              ) {
                                handleItemChange(index, "qty", val);
                              }
                            }}
                            className="w-24 border rounded px-2 py-1 text-center focus:outline-none focus:ring-2 focus:ring-blue-400"
                            // Remove 'required' so the browser doesn't block 0 or empty fields
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Sticky Save Button */}
              <div className="sticky bottom-0 bg-white pt-3 pb-6">
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 rounded shadow"
                >
                  {loading ? "Saving..." : "Save DC In"}
                </button>
              </div>
            </form>
          </div>
        </>
      )}

      {/* VIEW MODAL */}
      {viewDc && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50">
          <div className="bg-white rounded shadow-lg w-[600px] max-h-[80vh] overflow-y-auto">
            {/* Header */}
            <div className="flex justify-between items-center p-4 border-b">
              <h3 className="font-bold text-lg">DC Details</h3>
              <button
                className="text-gray-500 hover:text-gray-700"
                onClick={() => setViewDc(null)}
              >
                ✕
              </button>
            </div>

            {/* Body */}
            <div className="p-4 space-y-3">
              <p>
                <span className="font-semibold">Vendor:</span>{" "}
                {viewDc.vendor_name}
              </p>
              <p>
                <span className="font-semibold">PO:</span> {viewDc.po_number}
              </p>
              <p>
                <span className="font-semibold">Channel:</span>{" "}
                {viewDc.delivery_channel}
              </p>

              {/* Items Section */}
              <div>
                <h4 className="font-semibold mt-3 mb-2">Items</h4>

                {viewDc.items && viewDc.items.length > 0 ? (
                  <table className="w-full border text-sm">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="border p-2 text-left">#</th>
                        <th className="border p-2 text-left">Item Name</th>
                        <th className="border p-2 text-left">Supplied Qty</th>
                        <th className="border p-2 text-left">Updated Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {viewDc.items.map((item, index) => (
                        <tr key={index}>
                          <td className="border p-2">{index + 1}</td>
                          <td className="border p-2">{item.item_name}</td>
                          <td className="border p-2">{item.supplied_qty}</td>
                          <td className="border p-2">
                            {new Date(item.updated_at).toLocaleDateString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p className="text-gray-500 text-sm">No items available</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DcInPage;
